import React, {useMemo, useState} from 'react';
import {
  Backdrop,
  Box,
  CardMedia,
  Fade,
  IconButton,
  IconButtonProps,
  Modal,
  Popover,
  PopoverProps,
} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import fileSize from 'filesize';
import HumanReadableTime from '../../base-components/StudioHumanReadableTime/HumanReadableTime';
import {StudioCanvasOverlay} from '../../base-components/StudioOverlay/StudioCanvasOverlay';
import {BoundingBoxType} from '../../base-components/StudioOverlay/StudioCanvasOverlay/StudioCanvasOverlay';
import {StudioImageNode} from '../../base-components/StudioOverlay/StudioImageNode/StudioImageNode';
import {useIntl} from 'react-intl';
import './ImagePreview.scss';
import {useStudioDatasetViewerContext} from '../../base-components/StudioDataset/StudioDatasetViewerProvider';
import {ItemType} from '../../base-components/StudioDataset/FileViewer';
import {BoxLabelProps} from '../../base-components/StudioAnnotator/types';
import {labelToBoxLabel} from '../../base-components/StudioAnnotator/util';
import {RootState} from '../../store';
import {useSelector} from 'react-redux';

export type AnnotatedImageProps = {
  src: string;
  width: number;
  height: number;
  maxWidth: number;
  maxHeight: number;
  item?: ItemType | null;
  showLabel?: boolean;
  annotation?: BoundingBoxType[];
  label?: string;
  onClick?: IconButtonProps['onClick'];
};
export type ImagePreviewProps = {
  src: string;
  width: number | undefined;
  height: number | undefined;
  item?: ItemType | null;
  filename?: string;
  lastEdited?: string;
  size?: number;
  showLabel?: boolean;
  annotation?: BoundingBoxType[];
  label?: string;
  onClick?: IconButtonProps['onClick'];
};

const MAX_IMAGE_SIZE_X = 800;
const MAX_IMAGE_SIZE_Y = 600;
const getScaleFactor = (from: number, to: number) => to / from;
const scaleAnnotation = (scaleX: number, scaleY: number, annotation: BoxLabelProps[]) =>
  annotation.map(({width, height, x, y, label}) => ({
    label,
    bbox: [(x || 0) * scaleX, y * scaleY, width * scaleX, height * scaleY],
  }));

export const AnnotatedImage = ({
  item,
  src,
  width = 0,
  maxWidth = 0,
  maxHeight = 0,
  showLabel = false,
}: AnnotatedImageProps) => {
  const {labelMap} = useStudioDatasetViewerContext();
  const metadata = useSelector((state: RootState) => state.datasetMetadata);
  const {categories: categoriesRaw} = metadata;
  const [labels, setLabels] = useState<BoxLabelProps[]>([]);
  const [adj, setAdj] = useState({
    width: 0,
    height: 0,
    scaleBy: 0,
  });

  // Convert Remote Label data to local format (BoxLabelProps)
  useMemo(() => {
    if (item && labelMap && categoriesRaw && maxWidth && maxHeight) {
      // Calculate scaling and dimension sizing
      const w = item.width || 0;
      const h = item.height || 0;
      const nMaxWidth = Math.min(maxWidth, w);
      const nMaxHeight = Math.min(maxHeight, h);
      const scaleFactor = Math.min(
        getScaleFactor(w, nMaxWidth),
        getScaleFactor(h, nMaxHeight)
      );
      setAdj({
        width: nMaxWidth,
        height: nMaxHeight,
        scaleBy: scaleFactor,
      });

      // Convert label info for UI consumtion
      const remoteLabels = labelMap[item.id];
      if (remoteLabels) {
        setLabels(
          (remoteLabels.map(i =>
            labelToBoxLabel(i, {width: w, height: h}, categoriesRaw?.categories)
          ) as BoxLabelProps[]) || []
        );
      } else {
        setLabels([]);
      }
    }
  }, [item, labelMap, categoriesRaw, maxWidth, maxHeight]);

  return (
    <StudioCanvasOverlay
      width={adj.width}
      height={adj.height}
      data={scaleAnnotation(adj.scaleBy, adj.scaleBy, labels)}
      showLabel={showLabel}
      selectOnMouseOver={true}
    >
      {Boolean(adj.width && adj.height) && (
        // Component insice the Overlay is a Konva image that allow smoother integration compared to standard <img>. This allows artifact to be drawn inside the canvas
        <StudioImageNode
          src={src}
          scaleBy={adj.width === width ? 1 : adj.scaleBy}
          size={320}
        />
      )}
    </StudioCanvasOverlay>
  );
};

export const ImagePreview = ({
  filename,
  label,
  lastEdited,
  onClick,
  size,
  width = 0,
  height = 0,
  ...rest
}: ImagePreviewProps) => {
  const intl = useIntl();
  return (
    <div className="dataset-view__image_preview">
      <CardMedia className="dataset-view__image_preview_content">
        <AnnotatedImage
          {...rest}
          width={width}
          height={height}
          maxWidth={MAX_IMAGE_SIZE_X}
          maxHeight={MAX_IMAGE_SIZE_Y}
        />
      </CardMedia>
      <IconButton className="dataset-view__close-button" onClick={onClick}>
        <Close fontSize="small" />
      </IconButton>
      <Box display="flex" className="dataset-view__metadata">
        <Box display="flex" flexDirection="column">
          <div className="dataset-view__filename">{filename}</div>
          {lastEdited && (
            <div className="dataset-view__last-edited">
              Last edited <HumanReadableTime date={lastEdited} />
            </div>
          )}
          {label && <div className="dataset-view__label">Label: {label}</div>}
        </Box>
        <Box display="flex">
          {size && size > 0 && (
            <div className="dataset-view__size">File size: {fileSize(size || 0)}</div>
          )}
        </Box>
        <Box display="flex">
          <div className="dataset-view__size">
            {intl.formatMessage({id: 'dataViewer.dimensions.size'}, {width, height})}
          </div>
        </Box>
      </Box>
    </div>
  );
};

// Uses anchore to put preview aligned with the element on target/caller
export type ImagePreviewPopupProps = ImagePreviewProps & {
  anchorEl: PopoverProps['anchorEl'];
  onClose?: PopoverProps['onClose'];
};

export const ImagePreviewPopup = ({
  anchorEl,
  onClose,
  ...rest
}: ImagePreviewPopupProps) => {
  return (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      disableScrollLock
    >
      <ImagePreview {...rest} />
    </Popover>
  );
};

export type ImagePreviewModalProps = ImagePreviewProps & {
  open: boolean;
  onClose?: PopoverProps['onClose'];
};

// Opens a  screen centered dialog for preview.
export const ImagePreviewModal = ({
  open = false,
  onClose,
  ...rest
}: ImagePreviewModalProps) => {
  return (
    <Modal
      aria-labelledby="spring-modal-title"
      aria-describedby="spring-modal-description"
      className="dataset-view__image_modal"
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div className="dataset-view__image_modal_content">
          <ImagePreview {...rest} />
        </div>
      </Fade>
    </Modal>
  );
};
